import {
  DocumentDetail,
  type DocumentDetail as DocumentDetailData,
} from "@/components/accounting/document-detail";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { Link, createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/accounting/invoices/$id` — one issued document.
 *
 * An issued document is never edited: a correction is a new, mirrored
 * document and it needs a stated reason. The other write only links a
 * report to an invoice issued outside Hub billing.
 */

export const Route = createFileRoute("/_app/accounting/invoices/$id")({
  loader: async ({ params }) => {
    try {
      return await api.get<DocumentDetailData>(
        `/api/accounting/documents/${params.id}?side=issued`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: Page,
});

function Page() {
  const document = Route.useLoaderData();
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const creditable =
    document.type === "invoice" &&
    document.status !== "cancelled" &&
    document.status !== "draft" &&
    (document.corrections?.length ?? 0) === 0;

  async function creditNote() {
    setBusy(true);
    try {
      await api.post(`/api/accounting/documents/${document.id}/credit-note`, { reason });
      toast({ title: t("accInvoices.creditNoteIssued") });
      setOpen(false);
      setReason("");
      await router.invalidate();
    } catch (err) {
      toast({
        title: t("accInvoices.creditNoteFailed"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DocumentDetail
        document={document}
        side="issued"
        backTo="/accounting/invoices"
        backLabel={t("accInvoices.title")}
        actions={
          <>
            {document.batch ? (
              <Button variant="outline" asChild>
                <Link to="/billing/$id" params={{ id: document.batch.id }}>
                  {t("accInvoices.openReport")}
                </Link>
              </Button>
            ) : (
              <BackfillReport document={document} />
            )}
            {creditable && (
              <Button variant="destructive" onClick={() => setOpen(true)}>
                {t("accInvoices.issueCreditNote")}
              </Button>
            )}
          </>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("accInvoices.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("accInvoices.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">{t("accInvoices.reason")}</span>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("accInvoices.reasonPlaceholder")}
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              {t("accInvoices.cancel")}
            </Button>
            <Button onClick={creditNote} disabled={busy || reason.trim().length < 3}>
              {busy ? t("accInvoices.issuing") : t("accInvoices.issueCreditNote")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The month before the invoice's taxable date: what a monthly invoice usually bills. */
function previousMonth(document: DocumentDetailData): { from: string; to: string } {
  const taxable = new Date(document.taxableOn ?? document.issuedOn);
  const from = new Date(Date.UTC(taxable.getUTCFullYear(), taxable.getUTCMonth() - 1, 1));
  const to = new Date(Date.UTC(taxable.getUTCFullYear(), taxable.getUTCMonth(), 0));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function BackfillReport({ document }: { document: DocumentDetailData }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(() => previousMonth(document));
  const [busy, setBusy] = useState(false);

  if (
    document.type !== "invoice" ||
    document.status === "draft" ||
    document.status === "cancelled" ||
    !document.customer
  ) {
    return null;
  }

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/api/accounting/documents/${document.id}/backfill-report`, {
        periodFrom: period.from,
        periodTo: period.to,
      });
      toast({ title: t("accInvoices.backfill.done") });
      setOpen(false);
      await router.invalidate();
    } catch (err) {
      toast({
        title: t("accInvoices.backfill.failed"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {t("accInvoices.backfill.action")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("accInvoices.backfill.title")}</DialogTitle>
            <DialogDescription>{t("accInvoices.backfill.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">{t("accInvoices.backfill.from")}</span>
              <input
                type="date"
                className="w-full rounded-md border bg-background px-3 py-2"
                value={period.from}
                onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">{t("accInvoices.backfill.to")}</span>
              <input
                type="date"
                className="w-full rounded-md border bg-background px-3 py-2"
                value={period.to}
                onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              {t("accInvoices.cancel")}
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !period.from || !period.to || period.from > period.to}
            >
              {busy ? t("accInvoices.backfill.working") : t("accInvoices.backfill.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
